import {
  VibeSdk,
  completePopupCallback,
  type ContentDocument,
  type VibeProfile,
  type VibeScope,
} from "../sdk";
import { POST_TEMPLATE_ID } from "../shared/apps";

declare const __VIBE_NODE_ORIGIN__: string;

interface PostPayload { text: string; published: boolean }
interface PublicPost { id: string; text: string; createdAt: string; updatedAt: string; canonicalUrl: string }
interface PublicFeed {
  profile: Pick<VibeProfile, "handle" | "displayName" | "rootDid" | "avatarDataUri" | "coverDataUri">;
  posts: PublicPost[];
  hasMore: boolean;
}

const scopes: VibeScope[] = [
  "profile:read",
  `content.read:${POST_TEMPLATE_ID}`,
  `content.write:${POST_TEMPLATE_ID}`,
  "feed.publish",
];
const root = document.querySelector<HTMLElement>("#app")!;
const sdk = new VibeSdk({ nodeOrigin: __VIBE_NODE_ORIGIN__ });

if (completePopupCallback()) {
  root.innerHTML = loading("Returning to Vibe Feed…");
} else {
  void start();
}

async function start(): Promise<void> {
  const requestedRoute = currentRoute();
  root.innerHTML = requestedRoute.handle ? loading("Opening Vibe Feed…") : signInHandoff();
  try {
    await sdk.completeRedirectCallback();
    const route = currentRoute();
    const resumed = scopes.every((scope) => sdk.hasToken(scope)) || await sdk.resume(scopes);
    if (!route.handle) {
      if (!resumed) await sdk.connectManyWithRedirect(scopes);
      const profile = await sdk.profile();
      history.replaceState(null, "", `/@${encodeURIComponent(profile.handle)}`);
      return renderFeed(profile.handle, true);
    }
    let owner = false;
    if (resumed) {
      const profile = await sdk.profile();
      owner = profile.handle === route.handle;
    }
    await renderFeed(route.handle, owner, route.postId);
  } catch (error) {
    if (currentRoute().handle) renderLanding(messageOf(error));
    else renderSignInFallback(messageOf(error));
  }
}

function renderLanding(error?: string): void {
  root.innerHTML = `<section class="landing"><div class="brand"><img class="brand-logo" src="/assets/vibe-feed-logo.png" alt="" />Vibe Feed</div><div class="eyebrow">Canonical by default</div><h1>Your posts begin with your identity.</h1><p class="lede">Write short posts under your Vibe identity, keep them private until they are ready, and publish a public home you control.</p><button id="connect" type="button">Manage my feed</button><p class="fine-print">Vibe Feed does not follow people, rank a timeline, or cross-post anywhere in this release.</p>${error ? `<p class="error">${h(error)}</p>` : ""}</section>`;
  bindConnect();
}

function signInHandoff(error?: string): string {
  return `<section class="sign-in-shell">
    <figure class="feed-showcase"><div class="showcase-art"><img src="/assets/vibe-feed-showcase.png" width="564" height="377" alt="A clean public-post feed and post composer" decoding="async" fetchpriority="high" /><span class="showcase-product" aria-hidden="true">Vibe Feed</span></div></figure>
    <section class="sign-in-copy">${feedWordmark()}<div class="eyebrow">Canonical by default</div><h1>${error ? "Sign in to your feed." : "Opening secure sign-in…"}</h1><p>Use your Vibe account to continue. Your password stays with Vibe Cloud and is never shared with Vibe Feed.</p>${error ? `<button id="connect" type="button">Try signing in again</button><p class="error">${h(error)}</p>` : `<div class="redirect-status"><span aria-hidden="true"></span>Taking you to Vibe Cloud</div>`}<p class="fine-print">Public profiles stay readable without signing in. Managing posts requires this app's explicit Vibe permissions.</p></section>
  </section>`;
}

function renderSignInFallback(error: string): void {
  root.innerHTML = signInHandoff(error);
  bindConnect();
}

function feedWordmark(): string {
  return `<div class="feed-wordmark" aria-label="Vibe Feed"><img src="/assets/vibe-feed-logotype.png" width="564" height="180" alt="" /><span aria-hidden="true">Vibe Feed</span></div>`;
}

async function renderFeed(handle: string, owner: boolean, postId?: string): Promise<void> {
  const publicData = await fetchPublicFeed(handle, postId);
  const drafts = owner ? (await sdk.content.query<PostPayload>({ templateId: POST_TEMPLATE_ID, limit: 100 })).documents : [];
  const visiblePosts = postId && "post" in publicData ? [publicData.post] : "posts" in publicData ? publicData.posts : [];
  const profile = publicData.profile;
  document.title = `${profile.displayName} (@${profile.handle}) · Vibe Feed`;
  root.innerHTML = `<header class="topbar"><a class="brand" href="/"><img class="brand-logo" src="/assets/vibe-feed-logo.png" alt="" />Vibe Feed</a>${owner ? `<span class="owner-badge">Your feed</span>` : `<button id="connect" class="secondary" type="button">Manage my feed</button>`}</header><section class="profile-card">${profile.coverDataUri ? `<img class="cover" src="${h(profile.coverDataUri)}" alt="" />` : `<div class="cover fallback"></div>`}<div class="profile-body">${profile.avatarDataUri ? `<img class="avatar" src="${h(profile.avatarDataUri)}" alt="${h(profile.displayName)}" />` : `<div class="avatar fallback" aria-hidden="true">${h(initials(profile.displayName))}</div>`}<div><h1>${h(profile.displayName)}</h1><p class="handle">@${h(profile.handle)}</p><p class="did"><span class="did-dot" aria-hidden="true"></span>${h(shortDid(profile.rootDid))}</p></div></div></section>${owner ? composer() : ""}<section class="feed-layout"><div><div class="section-heading"><div><div class="eyebrow">${postId ? "Canonical post" : "Public feed"}</div><h2>${postId ? "Published post" : "Posts"}</h2></div>${owner && !postId ? `<span>${drafts.filter((post) => !post.payload?.published).length} unpublished</span>` : ""}</div><div class="post-list">${visiblePosts.length ? visiblePosts.map(publicPostTemplate).join("") : `<div class="empty">No published posts yet.</div>`}</div></div>${owner ? `<aside><div class="aside-card"><div class="eyebrow">Your posts</div><h2>Published & unpublished</h2><div class="draft-list">${drafts.length ? drafts.map(ownerPostTemplate).join("") : `<p>No drafts yet.</p>`}</div></div><div class="aside-card distribution"><strong>Distribution</strong><p>Vibe is canonical. Bluesky can be added later as an optional projection; nothing is cross-posted in this release.</p></div></aside>` : ""}</section>`;
  bindConnect();
  if (owner) bindOwnerActions(drafts);
}

function composer(): string {
  return `<section class="composer"><form id="post-form"><label for="post-text">New post</label><textarea id="post-text" name="text" maxlength="500" required placeholder="What do you want to share?"></textarea><div class="composer-actions"><label class="publish-choice"><input name="published" type="checkbox" checked /> Publish now</label><span id="counter">0 / 500</span><button type="submit">Post to Vibe</button></div><p id="post-error" class="error" hidden></p></form></section>`;
}

function publicPostTemplate(post: PublicPost): string {
  return `<article class="post"><p>${h(post.text)}</p><footer><time datetime="${h(post.updatedAt)}">${h(formatDate(post.updatedAt))}</time><a href="${h(new URL(post.canonicalUrl).pathname)}">Canonical link</a></footer></article>`;
}

function ownerPostTemplate(post: ContentDocument<PostPayload>): string {
  if (!post.payload) return "";
  return `<article class="draft"><p>${h(post.payload.text)}</p><div><span class="state ${post.payload.published ? "published" : "private"}">${post.payload.published ? "Published" : "Unpublished"}</span><button class="text-button toggle-post" data-id="${h(post.entityId)}" type="button">${post.payload.published ? "Unpublish" : "Publish"}</button><button class="text-button delete-post" data-id="${h(post.entityId)}" type="button">Delete</button></div></article>`;
}

function bindOwnerActions(posts: ContentDocument<PostPayload>[]): void {
  const textarea = document.querySelector<HTMLTextAreaElement>("#post-text")!;
  const counter = document.querySelector<HTMLElement>("#counter")!;
  textarea.addEventListener("input", () => { counter.textContent = `${Array.from(textarea.value).length} / 500`; });
  document.querySelector<HTMLFormElement>("#post-form")!.addEventListener("submit", async (event) => {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement;
    const button = form.querySelector<HTMLButtonElement>("button[type=submit]")!;
    const error = form.querySelector<HTMLElement>("#post-error")!;
    const data = new FormData(form);
    button.disabled = true;
    try {
      await sdk.content.save<PostPayload>({
        templateId: POST_TEMPLATE_ID,
        payload: { text: String(data.get("text") ?? "").trim(), published: data.get("published") === "on" },
        version: 0,
      });
      await renderFeed((await sdk.profile()).handle, true);
    } catch (cause) {
      error.hidden = false;
      error.textContent = messageOf(cause);
      button.disabled = false;
    }
  });
  document.querySelectorAll<HTMLButtonElement>(".toggle-post").forEach((button) => button.addEventListener("click", async () => {
    const post = posts.find((candidate) => candidate.entityId === button.dataset.id);
    if (!post?.payload) return;
    button.disabled = true;
    await sdk.content.save<PostPayload>({ templateId: POST_TEMPLATE_ID, entityId: post.entityId, payload: { ...post.payload, published: !post.payload.published }, version: post.version });
    await renderFeed((await sdk.profile()).handle, true);
  }));
  document.querySelectorAll<HTMLButtonElement>(".delete-post").forEach((button) => button.addEventListener("click", async () => {
    const post = posts.find((candidate) => candidate.entityId === button.dataset.id);
    if (!post || !confirm("Delete this post and its public copy?")) return;
    button.disabled = true;
    await sdk.content.delete({ templateId: POST_TEMPLATE_ID, entityId: post.entityId, version: post.version });
    await renderFeed((await sdk.profile()).handle, true);
  }));
}

function bindConnect(): void {
  document.querySelector<HTMLButtonElement>("#connect")?.addEventListener("click", () => void sdk.connectManyWithRedirect(scopes));
}

async function fetchPublicFeed(handle: string, postId?: string): Promise<PublicFeed | { profile: PublicFeed["profile"]; post: PublicPost }> {
  const path = postId
    ? `/api/public/feed/${encodeURIComponent(handle)}/posts/${encodeURIComponent(postId)}`
    : `/api/public/feed/${encodeURIComponent(handle)}?limit=100`;
  const response = await fetch(new URL(path, sdk.nodeOrigin));
  if (response.status === 404) throw new Error("This public Vibe Feed was not found.");
  if (!response.ok) throw new Error("The public feed is temporarily unavailable.");
  return response.json() as Promise<PublicFeed | { profile: PublicFeed["profile"]; post: PublicPost }>;
}

function currentRoute(): { handle?: string; postId?: string } {
  const match = /^\/@([^/]+)(?:\/posts\/([^/]+))?\/?$/u.exec(location.pathname);
  return match ? { handle: decodeURIComponent(match[1]!), postId: match[2] ? decodeURIComponent(match[2]) : undefined } : {};
}

function loading(message: string): string { return `<section class="loading"><div class="brand"><img class="brand-logo" src="/assets/vibe-feed-logo.png" alt="" />Vibe Feed</div><p>${h(message)}</p></section>`; }
function formatDate(value: string): string { return new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
function initials(value: string): string { return value.split(/\s+/u).slice(0, 2).map((part) => part[0] ?? "").join("").toUpperCase(); }
function shortDid(did: string): string { return did.length > 30 ? `${did.slice(0, 18)}…${did.slice(-8)}` : did; }
function messageOf(error: unknown): string { return error instanceof Error ? error.message : "Vibe Feed could not complete that action."; }
function h(value: string): string { return value.replace(/[&<>"']/gu, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[character]!); }
